import Analytics from '@/mixins/Analytics';
import eventNames from '@/constants/analyticsEventNames';
import parseQueryParamValue from '@/utilities/parseQueryParamValue';

import redirectAppStoreIfMobile from '@/utilities/redirectAppStoreIfMobile';
import store from '../../store';
import { keepDealerIdQueryString, myAcvRoutes } from './myAcv.routes';

const faUri = import.meta.env.VITE_REGISTRATION_FORM_ASSEMBLY_URI;

/** Provide capital routes from CPL as a fallback in case remote is unavailable */
const capitalRoutes = await import('capital/routes')
  .then((data) => data.default)
  .catch((e) => {
    Analytics.methods.noticeError('CAPITAL ROUTES FAILED', e);
    return [];
  });

const inventoryRoutes = await import('inventory/routes')
  .then((data) => data.default)
  .catch(() => null);

async function getMyAcvSendToAuctionComponent() {
  const microfrontendEnabled = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.myInventoryUseMicrofrontend,
  );

  const route = microfrontendEnabled && inventoryRoutes
    ? inventoryRoutes.SEND_TO_AUCTION_BULK_LAUNCH.component
    : import('@/views/MyAcv/MyAcvSendToAuction.vue');

  return route;
}
async function enableSrpRefresh() {
  const isSrpRefresh = await store.dispatch(
    'getFlag',
    store.state.featureFlags.featureFlagSlugs.enableSrpRefresh,
  );

  await store.dispatch('marketplaceView/getSrpRefreshPreference');
  const srpRefreshPreference = store.getters['marketplaceView/srpRefreshPreference'];

  return isSrpRefresh && srpRefreshPreference;
}

const routes = [
  {
    path: '/',
    redirect: 'login',
  },
  {
    path: '/universal-links/savedAuctions',
    redirect(to) {
      if (to.query.savedAuctionId) {
        return redirectAppStoreIfMobile(`/saved/${to.query.savedAuctionId}`);
      }
      return redirectAppStoreIfMobile('/myacv/saved/');
    },
  },
  {
    path: '/universal-links/search',
    redirect(to) {
      if (to.query.auctionId) {
        return redirectAppStoreIfMobile(`/auction/${to.query.auctionId}`);
      }
      return redirectAppStoreIfMobile('/auction/');
    },
  },
  {
    path: '/marketplace/:auctionDetailAuctionId?',
    name: 'Marketplace',
    component: () => import('@/views/Marketplace/Marketplace.vue'),
    meta: {
      title: 'Marketplace',
      showFooter: false,
      useLightUi: false,
      hasMobileTopBarHeader: true,
      allowAuctionCommonModals: true,
    },
    props: (route) => {
      const auctionDetailAuctionId = parseInt(
        route.params.auctionDetailAuctionId,
        10,
      );
      return {
        auctionDetailAuctionId: !Number.isNaN(auctionDetailAuctionId)
          ? auctionDetailAuctionId
          : undefined,
      };
    },
    async beforeEnter(to, from, next) {
      if (!await enableSrpRefresh()) {
        next({
          name: 'Search',
          params: to.params,
          query: to.query,
          replace: true,
        });
        return;
      }
      const providedLaneString = parseQueryParamValue(to.query.l, '');

      // when provided an id and there was no from path, this was a direct navigation
      // which should navigate to the auction route for that id instead
      if (to.params.auctionDetailAuctionId && (from.path === '/' || from.name === 'login')) {
        next({
          name: 'auction',
          params: {
            id: to.params.auctionDetailAuctionId,
          },
          query: to.query,
        });
      } else if (to.query.at
        || (to.query.l && !Array.isArray(providedLaneString))
      ) {
        // when there are search specific params, adjust them for marketplace
        const adjustedQuery = to.query;

        delete adjustedQuery.at; // remove at param

        // if the l param is not an array, stringify a string array for that lane
        const laneToUse = to.query.l === 'no_reserve_preview'
          ? 'guaranteed_sale'
          : to.query.l;

        adjustedQuery.l = JSON.stringify([laneToUse]);

        next({
          path: '/marketplace',
          query: adjustedQuery,
          replace: true,
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/search/:auctionDetailAuctionId?',
    name: 'Search',
    component: () => import('@/views/Search/Search.vue'),
    meta: {
      title: 'Search Auctions',
      showFooter: false,
      useLightUi: true,
      hasMobileTopBarHeader: true,
      allowAuctionCommonModals: true,
    },
    props: (route) => {
      const auctionDetailAuctionId = parseInt(
        route.params.auctionDetailAuctionId,
        10,
      );
      return {
        auctionDetailAuctionId: !Number.isNaN(auctionDetailAuctionId)
          ? auctionDetailAuctionId
          : undefined,
      };
    },
    async beforeEnter(to, from, next) {
      if (await enableSrpRefresh()) {
        next({
          name: 'Marketplace',
          params: to.params,
          query: to.query,
          replace: true,
        });
        return;
      }
      const providedLaneString = parseQueryParamValue(to.query.l, '');

      const toQueryParamsCount = Object.keys(to.query).length;
      // when provided an id and there was no from path, this was a direct navigation
      // when provided an id and from name is login it was direct navigation of logged-out user
      // both should navigate to the auction route for that id instead
      if (to.params.auctionDetailAuctionId && (from.path === '/' || from.name === 'login')) {
        next({
          name: 'auction',
          params: {
            id: to.params.auctionDetailAuctionId,
          },
          query: to.query,
        });
      } else if (!toQueryParamsCount) {
        const query = { l: 'live' };
        if (store.state.authorization.userAppliedSavedSearchIds.length) {
          query.s = JSON.stringify(
            store.state.authorization.userAppliedSavedSearchIds,
          );
        }

        next({
          path: '/search',
          query,
          replace: true,
        });
      } else if (
        (to.query.l && Array.isArray(providedLaneString))
        || to.query.p
        || to.query.o
      ) {
        // when there are new marketplace specific parameters, adjust them for search
        const adjustedQuery = to.query;

        delete adjustedQuery.p;
        delete adjustedQuery.o;

        // if the l param is an array of one entry, use that lane as the selected lane
        if (providedLaneString.length === 1) {
          adjustedQuery.l = providedLaneString?.[0] === 'guaranteed_sale'
            ? 'no_reserve_preview'
            : providedLaneString?.[0] || 'live';
        } else {
          adjustedQuery.l = 'live';
        }

        next({
          path: '/search',
          query: adjustedQuery,
          replace: true,
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/jwt-embed',
    name: 'JWTEmbed',
    component: () => import('@/views/Jwt/JwtEmbed.vue'),
    meta: {
      title: 'JWT Embed',
      allowUnauthorized: true,
      showHeader: false,
      showFooter: false,
      useLightUi: true,
      allowableRoutes: {
        MyAcvActiveBuying: '/myacv/active/buying',
        MyAcvSellerPerformance: '/myacv/performance',
        MyAcvInvoices: '/myacv/billing/invoices',
        PendingProxy: '/myacv/pendingproxy',
        Saved: '/myacv/saved',
        MarketReport: '/market-report',
        RequestInspectionNew: '/myacv/request-inspection/new',
        CapitalPortal: '/capital',
        Inventory: '/myacv/myinventory',
        MyAcvGuaranteedSale: '/myacv/guaranteed-sale',
      },
    },
  },
  {
    path: '/titles',
    name: 'MyAcvDigitalClerking',
    component: () => import('@/views/DigitalClerking/AcvDigitalClerk.vue'),
    redirect: '/titles/open',
    meta: {
      showHeader: true,
      shortTitle: 'Title Hub',
      simplifiedHeader: true,
      logoAlt: 'Title Hub Logo',
      showFooter: true,
      useLightUi: false,
      title: 'Title Hub',
      description: 'Manage open and closed titles',
      sellerOnly: true,
      displayName: 'Title Hub',
      hasExport: false,
      divider: true,
    },
    children: [
      {
        name: 'OpenItems',
        path: 'open',
        component: () => import('@/views/DigitalClerking/OpenItems.vue'),
        props: {
          viewType: 'open',
        },
        async beforeEnter(to, from, next) {
          const titlesRedesign = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.urgentTitleRedesign,
          );

          if (titlesRedesign) {
            next({ name: 'MyAcvTitleHub', params: { tab: 'urgent' } });
          }

          next();
        },
        meta: {
          showHeader: true,
          simplifiedHeader: true,
          logoAlt: 'Title Hub Logo',
          hideFromView: true,
          showFooter: true,
          useLightUi: true,
        },
      },
      {
        name: 'ClosedItems',
        path: 'closed',
        component: () => import('@/views/DigitalClerking/ClosedItems.vue'),
        async beforeEnter(to, from, next) {
          const titlesRedesign = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.urgentTitleRedesign,
          );

          if (titlesRedesign) {
            next({ name: 'MyAcvTitleHub', params: { tab: 'closed' } });
          }

          next();
        },
        props: {
          viewType: 'closed',
        },
        meta: {
          showHeader: true,
          simplifiedHeader: true,
          logoAlt: 'Title Hub Logo',
          hideFromView: true,
          showFooter: true,
          useLightUi: true,
        },
      },
    ],
  },
  {
    path: '/market-report',
    name: 'MarketReport',
    component: () => import('@/views/MarketReport/MarketReport.vue'),
    props: () => ({
      user: store.getters.currentUser,
      auctionLinksRedirect: !!window?.acvNative,
    }),
    meta: {
      title: 'Market Report',
      hasMobileTopBarHeader: true,
    },
  },
  {
    path: '/market-report/embed',
    name: 'MarketReportEmbed',
    component: () => import('@/views/MarketReport/MarketReportEmbedWrapper.vue'),
    meta: {
      allowUnauthorized: true,
      showHeader: false,
      showFooter: false,
      title: 'Market Report',
    },
  },
  {
    path: '/auction-access',
    redirect: '/registration',
  },
  {
    path: '/registration',
    component: () => import('@/views/AuctionAccess/AuctionAccess.vue'),
    title: 'ACV Registration with Auction Access',
    meta: {
      showFooter: false,
      allowUnauthorized: true,
      showHeader: false,
    },
    children: [
      {
        name: 'Auction Access Landing',
        path: '',
        component: () => import('@/components/AuctionAccess/AuctionAccessLanding.vue'),
        meta: {
          title: 'What is AuctionAccess?',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: true,
        },
        beforeEnter(to, from, next) {
          next();
        },
      },
      {
        name: 'Registration Form',
        path: '/registration-form',
        component: () => import('@/components/AuctionAccess/AuctionAccessForm.vue'),
        meta: {
          title: 'Provide your AuctionAccess Information',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: true,
        },
      },
      {
        path: '/auction-access-form',
        redirect: '/registration-form',
      },
      {
        name: 'Auction Access Dealerships',
        path: '/registration-dealerships',
        component: () => import('@/components/AuctionAccess/AuctionAccessDealerships.vue'),
        meta: {
          title: 'AuctionAccess Dealership Select',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: true,
        },
      },
      {
        path: '/auction-access-dealerships',
        redirect: '/registration-dealerships',
      },
    ],
  },
  {
    path: '/sso-redirect',
    component: () => import('@/views/Sso/SsoRedirect.vue'),
    title: 'Single Sign-On Redirect',
    meta: {
      showFooter: false,
      ssoRoute: true,
      allowUnauthorized: true,
    },
    async beforeEnter(to, from, next) {
      if (store.getters.isAuthorized) {
        next({
          path: '/myacv/request-inspection/new',
          replace: true,
        });
      } else {
        await store.dispatch('clearUser');
        next();
      }
    },
    children: [
      {
        name: 'SSO Authentication',
        path: '',
        component: () => import('@/views/Sso/SsoAuthentication.vue'),
        meta: {
          title: 'SSO Authentication',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: false,
          ssoRoute: true,
        },
      },
      {
        name: 'SSO Error',
        path: '/sso-error',
        component: () => import('@/views/Sso/SsoError.vue'),
        meta: {
          title: 'SSO Error',
          allowUnauthorized: true,
          showFooter: false,
          showHeader: false,
          ssoRoute: true,
        },
      },
    ],
  },
  {
    name: 'password',
    path: '/password',
    component: () => import('@/views/Login/SetPassword.vue'),
    beforeEnter(to, from, next) {
      if (!to.query.token) {
        next({ path: '/login' });
      } else {
        next();
      }
    },
    meta: {
      title: 'Reset Password',
      allowUnauthorized: true,
      showFooter: false,
      showRegistrationHeaderButtons: false,
    },
  },
  {
    name: 'forgot',
    path: '/forgot',
    component: () => import('@/views/Login/ForgotPassword.vue'),
    meta: {
      title: 'Forgot Password',
      allowUnauthorized: true,
      showFooter: false,
      showRegistrationHeaderButtons: false,
    },
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    children: [
      {
        name: 'login',
        path: '',
        component: () => import('@/views/Login/LoginContent.vue'),
        meta: {
          title: 'Login',
          allowUnauthorized: true,
          showFooter: false,
        },
        async beforeEnter(to, from, next) {
          if (store.getters.refreshToken?.length > 0) {
            try {
              await store.dispatch('refreshJwtTokens');
            } catch (err) {
              // do nothing
            }
          }
          if (store.getters.isAuthorized) {
            next({
              name: 'Search',
              replace: true,
              showFooter: false,
            });
          } else {
            await store.dispatch('clearUser');
            next();
          }
        },
      },
    ],
  },
  {
    path: '/auction/:id',
    name: 'auction',
    component: () => import('@/views/Auction.vue'),
    props: (route) => ({
      id: parseInt(route.params.id, 10),
      performAction: Object.prototype.hasOwnProperty.call(
        route.query,
        'action',
      ),
    }),
    meta: {
      title: 'Auction',
      showBackButton: true,
      allowAuctionCommonModals: true,
    },
    /* Disabling for now, but uncomment below when if want to start
     * redirecting legacy VDP to Search VDP - ETC
     */
    // async beforeEnter(to, from, next) {
    //     const {
    //       params: {
    //         id: auctionDetailAuctionId,
    //       },
    //     } = to;
    //     next({
    //       name: 'Search',
    //       params: {
    //         auctionDetailAuctionId,
    //       },
    //       replace: true,
    //   });
    //   }
    // },
  },
  {
    path: '/saved/:id',
    name: 'saved',
    component: () => import('@/views/Auction.vue'),
    async beforeEnter(to, from, next) {
      await keepDealerIdQueryString(to, from, next);
    },
    props: (route) => ({
      id: parseInt(route.params.id, 10),
      performAction: Object.prototype.hasOwnProperty.call(
        route.query,
        'action',
      ),
    }),
    meta: {
      title: 'Saved Auction',
    },
  },
  // TODO - Redirect to /settings/proxybids when settings gets released - ETC
  {
    path: '/proxybids',
    redirect: '/settings/proxybids',
  },
  {
    path: '/auctions/outstanding',
    component: () => import('@/views/AuctionLists/Outstanding.vue'),
    children: [
      {
        name: 'BuyingOutstanding',
        path: '',
        component: () => import('@/views/AuctionLists/AuctionList.vue'),
        props: {
          showAutoComplete: false,
        },
        meta: {
          title: 'Outstanding Auctions',
          linkText: 'Outstanding<span class="hidden-xs">&nbsp;Auctions</span>',
          hideTab: true,
        },
        async beforeEnter(to, from, next) {
          const forceAuctionsFinalize = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.forceAuctionsFinalize,
          );
          const hasOutstandingAuctions = store.getters['auctions/hasOutstandingAuctions'];
          if (forceAuctionsFinalize && hasOutstandingAuctions) {
            next();
          } else {
            next('/auctions');
          }
        },
      },
    ],
  },
  {
    path: '/settings',
    component: () => import('@/views/Settings/Settings.vue'),
    children: [
      {
        name: 'Settings',
        path: '',
        redirect: '/settings/account',
      },
      {
        path: 'account',
        name: 'MyAccount',
        component: () => import('@/views/Settings/MyAccount.vue'),
        meta: {
          title: 'My Account',
          adminOnly: false,
        },
      },
      {
        path: 'addresses',
        name: 'MyAddresses',
        component: () => import('@/views/Settings/MyAddresses.vue'),
        meta: {
          title: 'Manage pickup and delivery addresses',
          adminOnly: true,
        },
        props: (route) => {
          const { showSuccess, addOrUpdate } = route.params;
          const orgId = route.params.id;
          const orgName = route.params.name;
          return {
            showSuccess,
            orgId,
            orgName,
            addOrUpdate,
          };
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'add-new-address',
        name: 'AddNewAddress',
        component: () => import('@/views/Settings/AddNewAddress.vue'),
        meta: {
          title: 'Add new address',
          adminOnly: true,
          sidebarHighlight: 'MyAddresses',
        },
        props: (route) => {
          const orgId = route.params.id;
          const orgName = route.params.name;
          return {
            orgId,
            orgName,
          };
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'edit-address',
        name: 'EditAddress',
        component: () => import('@/views/Settings/EditAddress.vue'),
        meta: {
          title: 'Edit address',
          adminOnly: true,
          sidebarHighlight: 'MyAddresses',
        },
        props: (route) => {
          const orgId = route.params.id;
          const orgName = route.params.name;
          const { addressId } = route.params;
          return {
            orgId,
            orgName,
            addressId,
          };
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'docs',
        name: 'RegistrationDocuments',
        component: () => import('@/views/Settings/RegistrationDocuments.vue'),
        meta: {
          title: 'Registration Documents',
          adminOnly: false,
        },
        async beforeEnter(to, from, next) {
          const dealerDocsUploadDisabled = await store.dispatch(
            'getFlag',
            store.state.featureFlags.featureFlagSlugs.dealerDocsUploadDisabled,
          );

          if (dealerDocsUploadDisabled) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'paymentmethod',
        name: 'Paymentmethod',
        component: () => import('@/views/Settings/PaymentMethod.vue'),
        meta: {
          title: 'Payment Method Settings',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'dealership',
        name: 'MyDealership',
        component: () => import('@/views/Settings/MyDealership.vue'),
        meta: {
          title: 'My Dealership',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'dashboard',
        name: 'ManageUsers',
        component: () => import('@/views/Settings/ManageUsers.vue'),
        meta: {
          title: 'Manage Users',
          adminOnly: true,
        },
        beforeEnter(to, from, next) {
          // redirect to /settings if user is not an admin
          if (
            !store.state.authorization.user
            || !store.state.authorization.user.isAdmin
          ) {
            next({ path: '/settings' });
          } else {
            next();
          }
        },
      },
      {
        path: 'proxybids',
        name: 'ProxyBidSettings',
        component: () => import('@/views/Settings/ProxyBidSettings.vue'),
        meta: {
          title: 'Proxy Bid Settings',
          adminOnly: false,
        },
      },
      {
        path: 'notification-settings',
        name: 'NotificationSettings',
        component: () => import('@/views/Settings/NotificationSettings.vue'),
        meta: {
          adminOnly: false,
        },
      },
      {
        path: 'sms-settings',
        name: 'SmsOptInSettings',
        component: () => import('@/views/Settings/SmsOptInSettings.vue'),
        meta: {
          adminOnly: false,
        },
      },
    ],
  },
  // Keeping routes in case any external links are pointed
  {
    path: '/register',
    redirect: (to) => ({
      path: '/registration',
      query: to.query,
    }),
  },
  {
    path: '/registration',
    children: [
      {
        path: '',
        redirect: '/registration/information',
        beforeEnter() {
          window.location.href = faUri;
        },
      },
      {
        path: 'existing',
        name: 'ExistingLead',
        meta: {
          allowUnauthorized: true,
          showFooter: false,
        },
        beforeEnter() {
          window.location.href = faUri;
        },
      },
      {
        path: 'information',
        name: 'Information',
        meta: {
          allowUnauthorized: true,
          showFooter: false,
        },
        beforeEnter() {
          window.location.href = faUri;
        },
      },
      {
        path: 'finished',
        name: 'Finished',
        meta: {
          allowUnauthorized: true,
          showFooter: false,
        },
        beforeEnter() {
          window.location.href = faUri;
        },
      },
    ],
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/Notifications.vue'),
    meta: {
      title: 'My Notifications',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/Logout.vue'),
    meta: {
      allowUnauthorized: true,
    },
  },
  {
    path: '/privacypolicy',
    name: 'dnsform',
    component: () => import('@/views/DnsForm.vue'),
    meta: {
      allowUnauthorized: true,
      title: 'Privacy Policy',
    },
  },
  {
    path: '/finalize',
    beforeEnter: (to, from, next) => {
      const auctionId = to.query.auction_id;
      next({
        path: `/auction/${auctionId}`,
        replace: true,
        query: { action: null },
      });
    },
  },
  {
    path: '/carfax-signin',
    name: 'CarfaxSigninCallBack',
    component: () => import('@/views/CarfaxSigninCallback/CarfaxSigninCallback.vue'),
    props: (route) => {
      const stateArray = route.query?.state?.split(',') ?? [];
      const [state, vin] = stateArray;
      const code = route.query?.code;
      return {
        state,
        code,
        vin,
      };
    },
  },
  {
    path: '/offboarding',
    name: 'Offboarding',
    component: () => import('@/views/Offboarding/Offboarding.vue'),
    beforeEnter(to, from, next) {
      if (store.getters.currentUser.active) {
        next();
      } else {
        next({ path: '/settings/account' });
      }
    },
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
  },
  {
    path: '/myacv/send-to-auction',
    name: 'MyAcvSendToAuction',
    component: getMyAcvSendToAuctionComponent,
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
  },
  {
    path: '/myacv/gsale-performance',
    name: 'MyAcvGsalePerformanceTable',
    component: () => import('@/views/MyAcv/MyAcvGsalePerformanceTable.vue'),
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
    beforeEnter(to, from, next) {
      if (store.getters.currentUser.active) {
        next();
      } else {
        next({ path: '/myacv/myinventory' });
      }
    },
  },
  {
    path: '/myacv/guaranteed-sale',
    name: 'MyAcvGuaranteedSale',
    component: () => import('@/views/MyAcv/MyAcvGuaranteedSale.vue'),
    async beforeEnter(to, from, next) {
      const myInventoryAddGuaranteedSaleOffers = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.myInventoryAddGuaranteedSaleOffers,
      );
      if (store.getters.currentUser.active
        && (myInventoryAddGuaranteedSaleOffers)) {
        next();
      } else {
        next({ path: '/myacv/myinventory' });
      }
    },
    meta: {
      useLightUi: false,
      hideFooterOverride: true,
    },
  },
  {
    path: '/capital',
    name: 'Capital',
    component: () => import('@/views/CapitalPortal/CapitalPortal.vue'),
    children: [
      ...capitalRoutes,
    ],
    meta: {
      title: 'Capital Portal',
      shortTitle: 'Capital Portal',
      description: 'ACV Capital Finance',
      linkText: 'Capital Portal',
      useLightUi: true,
    },
    async beforeEnter(to, from, next) {
      // Checking if isCapitalUser is defined in the user object
      const isCapitalUserDefined = Object.prototype.hasOwnProperty.call(store.getters.currentUser, 'isCapitalUser');

      if (from.name === 'login' || !isCapitalUserDefined || store.getters.currentUser.capitalUserFailed) {
        await store.dispatch('setIsCapitalUser');
        Analytics.methods.track(eventNames.capital.portal.existsRetry, {
          status: store.getters.currentUser.capitalUserFailed ? 'failed' : 'success',
          from: 'ACV Capital Tab',
        });
      }

      const user = store.getters.currentUser;

      if (user?.capitalUserFailed) {
        next({ path: '/capital/portal-error' });
        return;
      }

      const dealerIdQuery = Number(to.query.dealer_id);

      const capitalShowAdvantageLeadGen = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.capitalShowAdvantageLeadGen,
      );

      const { isCapitalUser } = store.getters.currentUser;

      const isCapitalWithFloorplanDealers = isCapitalUser
        && user.capitalDealerSelectionList.length > 0;

      const isCapitalAdvantageOnly = capitalShowAdvantageLeadGen
        && isCapitalUser
        && !isCapitalWithFloorplanDealers
        // This check if all dealers in the list of capital dealers have only ADVANTAGE product line
        && Object.entries(user?.capitalUser?.dealerships)
          ?.every(([, value]) => value?.product_lines?.buyer?.every((product) => product === 'ADVANTAGE'));

      if (dealerIdQuery && store.getters.isAuthorized) {
        if (isCapitalWithFloorplanDealers) {
          Analytics.methods.track(
            eventNames.capital.landing.salesRepURLAccessed,
            {
              user_id: user.user_id,
              dealer_id: dealerIdQuery,
              error: 'The dealer is capital',
            },
          );
        }
      }

      if (!isCapitalUser || isCapitalAdvantageOnly) {
        next({ path: '/capital/landing-page' });
      } else if (!isCapitalWithFloorplanDealers) {
        next({ path: '/search' });
      } else {
        next();
      }
    },
  },
  {
    path: '/capital/landing-page',
    name: 'NonCapitalUserLandingPage',
    component: () => import('@/views/CapitalPortal/NonCapitalUserLandingPage.vue'),
    async beforeEnter(to, from, next) {
      if (store.getters.currentUser?.capitalUserFailed) {
        await store.dispatch('setIsCapitalUser');
        Analytics.methods.track(eventNames.capital.portal.existsRetry, {
          status: store.getters.currentUser.capitalUserFailed ? 'failed' : 'success',
          from: 'URL Landing Page',
        });
      }

      const capitalShowAdvantageLeadGen = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.capitalShowAdvantageLeadGen,
      );

      const user = store.getters.currentUser;

      const { isCapitalUser } = store.getters.currentUser;

      const isCapitalWithFloorplanDealers = isCapitalUser
        && user.capitalDealerSelectionList.length > 0;

      const isCapitalAdvantageOnly = capitalShowAdvantageLeadGen
        && isCapitalUser
        && !isCapitalWithFloorplanDealers
        // This check if all dealers in the list of capital dealers have only ADVANTAGE product line
        && Object.entries(user?.capitalUser?.dealerships)
          ?.every(([, value]) => value?.product_lines?.buyer?.every((product) => product === 'ADVANTAGE'));

      if (user?.capitalUserFailed) {
        next({ path: '/capital/portal-error' });
      } else if (isCapitalWithFloorplanDealers) {
        next('/capital');
      } else if (!isCapitalUser || isCapitalAdvantageOnly) {
        next();
      } else {
        next({ path: '/search' });
      }
    },
    meta: {
      useLightUI: true,
    },
  },
  {
    path: '/capital/portal-error',
    name: 'CapitalPortalError',
    component: () => import('@/views/CapitalPortal/CapitalPortalError.vue'),
    beforeEnter(to, from, next) {
      const user = store.getters.currentUser;

      if (user?.capitalUserFailed) {
        next();
      } else {
        next({ path: '/search' });
      }
    },
    meta: {
      useLightUI: true,
    },
  },
  // Keeping route in case any external links are still pointing to credit-balance
  {
    path: '/capital/credit-balance',
    redirect: '/capital/account-details',
  },
  // Keeping route in case any external links are still pointing to portfolio
  {
    path: '/capital/portfolio',
    redirect: '/capital/account-details',
  },
  {
    path: '/transportation-hub',
    component: () => import(
      /* webpackChunkName: "Transportation" */ '@/views/TransportationHub/TransportationHub.vue'
    ),
    name: 'transportation-hub',
    meta: {
      title: 'Transportation Hub',
    },
    async beforeEnter(to, from, next) {
      const requestOffPlatformTransportation = await store.dispatch(
        'getFlag',
        store.state.featureFlags.featureFlagSlugs.requestOffPlatformTransportation,
      );

      if (requestOffPlatformTransportation) {
        next();
      } else {
        next({ path: '/search' });
      }
    },
  },
  {
    path: '/rewards',
    component: () => import('@/views/Loyalty/LoyaltySignup.vue'),
    async beforeEnter(to, from, next) {
      await store.dispatch('checkEligibilityToRewardsProgram');

      if (store.state.rewards.isRewardsProgramEligible) {
        next();
      } else {
        next({ path: '/' });
      }
    },
  },
  {
    path: '/request-cash-flow/expired',
    name: 'RequestCashFlowExpired',
    component: () => import('@/views/CapitalPortal/RequestCashFlowExpired.vue'),
    meta: {
      title: 'Capital Finicity Request Expired',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/request-cash-flow',
    name: 'AccessFinicityPortal',
    component: () => import('@/views/CapitalPortal/AccessFinicityPortal.vue'),
    meta: {
      title: 'Capital Finicity Request Cash Flow',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/finicity-successfully-connected',
    name: 'FinicitySuccessfullyConnected',
    component: () => import('@/views/CapitalPortal/FinicitySuccessfullyConnected.vue'),
    meta: {
      title: 'Capital Finicity Request Successfully Connected',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/request-cash-flow/updated-link',
    name: 'RequestCashFlowUpdatedLink',
    component: () => import('@/views/CapitalPortal/RequestCashFlowUpdatedLink.vue'),
    meta: {
      title: 'Capital Finicity Request Updated Link',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  {
    path: '/finicity-error-handler',
    name: 'FinicityErrorHandler',
    component: () => import('@/views/CapitalPortal/FinicityErrorHandler.vue'),
    meta: {
      title: 'Capital Finicity Error Handler',
      allowUnauthorized: true,
      showHeader: false,
    },
  },
  // NOTE: Leave this route at the end -RT
  // https://router.vuejs.org/guide/essentials/history-mode.html#caveat
  {
    path: '*',
    redirect: '/login',
  },
].concat(myAcvRoutes);

export default routes;
